import { useEffect, useRef } from 'react';

import { Icon } from '../components/Icon.js';
import { RichTextEditor, type RichTextEditorHandle } from '../components/RichTextEditor.js';
import styles from './EventMessageField.module.css';

interface EventMessageFieldProps {
  /** What has been typed so far, for writing back after a remount. */
  value: string;
  onChange: (markup: string) => void;
  onRemove: () => void;
}

/**
 * A covering note to go into the guests' chats beside the invite.
 *
 * Optional, and offered rather than asked for: a Chat Day invite with nothing
 * written under it is a perfectly normal thing to send, so the box only exists
 * once somebody wants it.
 *
 * Two messages, not one. The invite is a record with a state on it that gets
 * answered or withdrawn; this is a person talking, and nothing should ever grey
 * it out or rewrite it. That is why it becomes its own chat bubble rather than
 * being appended to the invite's text, and why it is not stored on the event.
 */
export function EventMessageField({ value, onChange, onRemove }: EventMessageFieldProps) {
  const editorRef = useRef<RichTextEditorHandle>(null);

  /**
   * Written back on mount, and only on mount.
   *
   * The editor is uncontrolled, so its words live in the DOM rather than in
   * state. Guarded on emptiness so opening a fresh field does not fight the
   * caret, and mount-only — writing back on every change is what makes a
   * home-grown editor jump the caret to the end mid-sentence, so `value` is
   * deliberately not a dependency.
   */
  useEffect(() => {
    if (value) editorRef.current?.setMarkup(value);
  }, []);

  return (
    <div className={styles.slot}>
      <RichTextEditor
        ref={editorRef}
        onChange={onChange}
        placeholder="Anything they should know? (optional)"
        autoFocus
        /*
         * Taking the box away sits at the end of the toolbar, which is the slot
         * this editor keeps for a control acting on the whole draft rather than
         * on the words in it. Above the box it needed a head where a label, a
         * sentence and a button shared one baseline at three sizes.
         */
        toolbarEnd={
          <button
            type="button"
            className={styles.remove}
            title="Remove message"
            aria-label="Remove message"
            onClick={onRemove}
          >
            <Icon name="close" size={15} />
          </button>
        }
      />
    </div>
  );
}
